/**
 * @classdesc The `CommunityType` class serves as the source of truth for current community types and their codes.
 */
export default class CommunityType {
    /**
     * Gets the instance for the Normal community type.
     * @returns {CommunityType} The instance for the Normal community type.
     */
    static get Normal() {
        return this.getInstance('SBC');
    }

    /**
     * Gets the instance for the Custom community type.
     * @returns {CommunityType} The instance for the Custom community type.
     */
    static get Custom() {
        return this.getInstance('CST');
    }

    /**
     * Gets the instance for the BuildOnYourLot community type.
     * @returns {CommunityType} The instance for the BuildOnYourLot community type.
     */
    static get BuildOnYourLot() {
        return this.getInstance('BYL');
    }

    /**
     * Gets the instance for the Modular community type.
     * @returns {CommunityType} The instance for the Modular community type.
     */
    static get Modular() {
        return this.getInstance('MFR');
    }

    /**
     * Gets the instance for the Condo community type.
     * @returns {CommunityType} The instance for the Condo community type.
     */
    static get Condo() {
        return this.getInstance('CON');
    }

    /**
     * Gets the instance for the Adult community type.
     * @returns {CommunityType} The instance for the Adult community type.
     */
    static get Adult() {
        return this.getInstance('ADU');
    }

    /**
     * Gets the instance for the TownHome community type.
     * @returns {CommunityType} The instance for the TownHome community type.
     */
    static get TownHome() {
        return this.getInstance('THM');
    }

    /**
     * Gets the instance for the MasterPlanned community type.
     * @returns {CommunityType} The instance for the MasterPlanned community type.
     */
    static get MasterPlanned() {
        return this.getInstance('MPC');
    }

    /**
     * Gets the instance for the CustomLocation community type.
     * @returns {CommunityType} The instance for the CustomLocation community type.
     */
    static get CustomLocation() {
        return this.getInstance('CSL');
    }

    /**
     * Converts a key to the corresponding community type instance.
     * @param {string} key The key representing a community type.
     * @returns {CommunityType|null} The community type instance or null if not found.
     */
    static fromKey(key) {
        const communityTypeMap = {
            SBC: CommunityType.Normal,
            CST: CommunityType.Custom,
            BYL: CommunityType.BuildOnYourLot,
            MFR: CommunityType.Modular,
            CON: CommunityType.Condo,
            ADU: CommunityType.Adult,
            THM: CommunityType.TownHome,
            MPC: CommunityType.MasterPlanned,
            CSL: CommunityType.CustomLocation,
        };
        return communityTypeMap[key] || null;
    }

    /**
     * Gets or creates an instance of the `CommunityType` class based on the provided key.
     * @param {string} key The key representing a community type.
     * @returns {CommunityType} The community type instance.
     */
    static getInstance(key) {
        if (!this.instances[key]) {
            this.instances[key] = new CommunityType(key);
        }
        return this.instances[key];
    }

    /**
     * Creates a new instance of the `CommunityType` class.
     * @param {string} name The name of the community type.
     */
    constructor(name) {
        this.name = name;
    }

    /**
     * Returns the name of the community type as a string.
     * @returns {string} The name of the community type.
     */
    toString() {
        return `${this.name}`;
    }
}

Object.defineProperty(CommunityType, 'instances', { value: {} });
